import { css, Global, useRouter } from '@obvio/app'
import { serverClient } from '@obvio/server'
import { EVENT_CAROUSEL_SELECT } from '@obvio/template'
import { useEffect } from 'react'

import { schema } from '@/api/schema'
import { Cursor, useCursorContext } from '@/components/Cursor'
import { EventPage } from '@/components/Event/EventPage'
import { EventPageVip, EVENT_SELECT } from '@/components/Event/EventPageVip'
import { Layout } from '@/components/Layout'
import { LayoutContentProvider } from '@/contexts/LayoutContentContext'

import type { Event } from '@/components/Event/EventPageVip'
import type { AllowUndefinedForOptional } from '@obvio/utils'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { ReactElement } from 'react'

export const getStaticPaths: GetStaticPaths = async () => {
  const client = await serverClient(schema, { isAdmin: true })

  const events = await client.query('getEvents', {
    args: {
      all: true,
    },
    select: {
      slug: true,
    },
  })

  const paths = events.map((event) => ({
    params: {
      slug: event.slug,
    },
  }))

  return {
    paths,
    fallback: false,
  }
}

type EventSerialized = AllowUndefinedForOptional<
  Omit<Event, 'date' | 'endDate'> & { date: string; endDate: string | null }
>

export const getStaticProps: GetStaticProps<EventSerialized, { slug: string }> =
  async (context) => {
    const client = await serverClient(schema, { isAdmin: true, ...context })

    const event = await client.query('getEvent', {
      args: {
        slug: context.params?.slug ?? '',
      },
      select: EVENT_SELECT,
    })

    if (!event) {
      return { notFound: true }
    }

    await client.query('getEvents', {
      select: EVENT_CAROUSEL_SELECT,
      args: {
        all: true,
      },
    })

    // serialized, this is due to date being Date
    const eventSerialized: EventSerialized = {
      ...event,
      date: event.date.toISOString(),
      endDate: event.endDate?.toISOString() ?? null,
    }

    return {
      ...client.ssrData(eventSerialized),
      revalidate: 1000,
    }
  }

const globalStyle = css`
  body {
    background-color: ${(theme) => theme.background};
  }
`

function Page(eventSerialized: EventSerialized): ReactElement {
  const router = useRouter()
  const { setValue } = useCursorContext()

  useEffect(() => {
    const handleRouteChange = () => setValue(null)

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events, setValue])

  const { isVip, ...event } = {
    ...eventSerialized,
    date: new Date(eventSerialized.date),
    endDate: eventSerialized.endDate ? new Date(eventSerialized.endDate) : null,
  }

  return (
    <LayoutContentProvider value="Main">
      <Global styles={globalStyle} />
      <Cursor key={event.id} kind={isVip ? 'white' : 'black'} />
      <Layout context={isVip ? 'eventVIP' : 'event'} dark={isVip}>
        {isVip ? <EventPageVip {...event} /> : <EventPage {...event} />}
      </Layout>
    </LayoutContentProvider>
  )
}

export default Page
